import { Box, Dialog, type DialogProps } from '@mui/material';
import { useId } from 'react';
import ModalBody from './components/ModalBody';
import ModalFooter from './components/ModalFooter';
import ModalHeader from './components/ModalHeader';
import { useModalResponsive } from './hooks/useModalResponsive';
import { MODAL_BODY_PADDING, MODAL_WIDTH } from './modal.constants';
import { useModalStyles } from './modal.styles';
import type { ModalProps } from './modal.types';

const Modal = ({
  isOpen,
  onClose,
  onExited,
  onEntered,
  isDismissable = true,
  size = 'standard',
  height,
  header,
  footer,
  slots,
  testId,
  bodyRef,
  children,
}: ModalProps) => {
  const { classes, cx } = useModalStyles();
  const { isFullScreen, isDrawer } = useModalResponsive(size);
  const titleId = useId();
  const paperClassName = cx(classes.paper, {
    [classes.drawerPaper]: isDrawer,
    [classes.fullScreenPaper]: isFullScreen,
  });
  const paperHeight = isFullScreen || isDrawer ? undefined : height;

  const handleClose: NonNullable<DialogProps['onClose']> = (_event, reason) => {
    if (!isDismissable && reason === 'backdropClick') {
      return;
    }

    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={false}
      fullScreen={isFullScreen}
      disableScrollLock={false}
      disableEscapeKeyDown={!isDismissable}
      aria-labelledby={titleId}
      test-id={testId}
      onTransitionExited={onExited}
      slotProps={{
        container: isDrawer ? { className: classes.drawerContainer } : undefined,
        transition: { onEntered },
        paper: {
          className: paperClassName,
          sx: {
            width: MODAL_WIDTH[size],
            height: paperHeight,
            pb: !footer && children != null ? MODAL_BODY_PADDING : undefined,
          },
        },
      }}
    >
      <ModalHeader {...header} titleId={titleId} onClose={onClose} />
      {slots?.headerBottom && <Box className={classes.headerBottom}>{slots.headerBottom}</Box>}
      {children != null && (
        <ModalBody bodyRef={bodyRef} hasFooter={!!footer}>
          {children}
        </ModalBody>
      )}
      {footer && <ModalFooter {...footer} />}
    </Dialog>
  );
};

export default Modal;
